import { useState, useEffect, useCallback } from 'react';
import { authenticatedFetch } from '../utils/api';
import { API_HOST } from '../utils/constants';
import { getEffectiveAssetMeta } from '../utils/guaVideoAssetStatus';
import { getProjectVideoProvider } from '../utils/projectRuntime';
import { isSeedance25Provider } from '../utils/seedance25Capabilities';

const EMPTY_ASSET_MAPS = Object.freeze({ statusMap: {}, assetMetaMap: {} });

/**
 * 构建 imageUrl → assetStatus 映射，用于在参考图上显示入库状态角标
 */
export const useAssetStatusMap = (projectId, options = {}) => {
  const assetProvider = options?.provider || getProjectVideoProvider(projectId, 'guavideo');
  const enabled = options?.enabled !== false && !isSeedance25Provider(assetProvider);
  const auto = options?.auto !== false && enabled;
  const [statusMap, setStatusMap] = useState({});
  const [assetMetaMap, setAssetMetaMap] = useState({});

  const normalizeUrl = useCallback((url) => {
    if (!url || typeof url !== 'string') return '';
    const value = url.trim();
    if (!value) return '';
    if (value.startsWith('http') || value.startsWith('data:')) return value;
    if (value.startsWith('/')) return `${API_HOST}${value}`;
    return value;
  }, []);

  const buildMap = useCallback(async () => {
    if (!enabled || !projectId) return EMPTY_ASSET_MAPS;
    const map = {};
    const metaMap = {};
    const put = (url, item) => {
      if (!url) return;
      const effective = getEffectiveAssetMeta(item, assetProvider);
      const { provider, status } = effective;
      if (!status) return;
      const meta = {
        guaVideoAssetId: item?.guaVideoAssetId || '',
        guaVideoAssetStatus: item?.guaVideoAssetStatus || '',
        engGuaVideoAssetId: item?.engGuaVideoAssetId || '',
        engGuaVideoAssetStatus: item?.engGuaVideoAssetStatus || '',
        guaOminiAssetId: item?.guaOminiAssetId || '',
        guaOminiAssetStatus: item?.guaOminiAssetStatus || '',
        dreaminaProxyAssetId: item?.dreaminaProxyAssetId || '',
        dreaminaProxyAssetStatus: item?.dreaminaProxyAssetStatus || '',
        assetId: effective.assetId,
        status,
        provider,
      };
      [url, normalizeUrl(url)].filter(Boolean).forEach((key) => {
        map[key] = status;
        metaMap[key] = meta;
      });
    };
    try {
      const [candidates, legacyChars, legacyScenes, legacyProps, resources] = await Promise.all([
        authenticatedFetch(`${API_HOST}/api/assets/candidates?projectId=${projectId}`).catch(() => null),
        authenticatedFetch(`${API_HOST}/api/characters?projectId=${projectId}&summary=true`).catch(() => []),
        authenticatedFetch(`${API_HOST}/api/scenes?projectId=${projectId}&summary=true`).catch(() => []),
        authenticatedFetch(`${API_HOST}/api/props?projectId=${projectId}&summary=true`).catch(() => []),
        authenticatedFetch(`${API_HOST}/api/resource/query`, {
          method: 'POST',
          body: JSON.stringify({ projectId: parseInt(projectId, 10) }),
        }).catch(() => []),
      ]);
      const process = (list) => {
        (list || []).forEach(item => {
          put(item.imageUrl, item);
          (item.variations || []).forEach(v => {
            put(v.imageUrl, v);
          });
        });
      };
      if (candidates && (Array.isArray(candidates.characters) || Array.isArray(candidates.scenes) || Array.isArray(candidates.props))) {
        process(candidates.characters);
        process(candidates.scenes);
        process(candidates.props);
      } else {
        process(legacyChars);
        process(legacyScenes);
        process(legacyProps);
      }
      (resources || []).forEach(item => {
        put(item.url, item);
        put(item.thumbnailUrl, item);
      });
    } catch { /* ignore */ }
    setStatusMap(map);
    setAssetMetaMap(metaMap);
    return { statusMap: map, assetMetaMap: metaMap };
  }, [assetProvider, enabled, normalizeUrl, projectId]);

  useEffect(() => {
    if (auto) buildMap();
  }, [auto, buildMap]);

  useEffect(() => {
    if (!auto) return undefined;
    const handleFocus = () => buildMap();
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [auto, buildMap]);

  return { ...(enabled ? { statusMap, assetMetaMap } : EMPTY_ASSET_MAPS), refreshStatusMap: buildMap };
};
